import { Currencies } from '@/entities/curency'
import {
  BaseTraderWithdrawModel,
  PaidWithdrawModel,
  StartPayingWithdrawModel,
  TraderAcceptedWithdrawModel,
  WithdrawStatus,
  WithdrawStatusButtons,
  WithdrawStatusIcons,
  WithdrawStatusTitles,
} from '@/features/withdraws/model'
import { PayoutButton } from '@/features/withdraws/ui/payoutButton/PayoutButton'
import { BaseCard } from '@/shared/components'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'
import { PayoutIcon } from '@/shared/components/icons/PayoutIcon'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { formatRequisite } from '@/shared/lib/requisite/formatRequisite'
import { getBankMeta } from '@/shared/utils/getBankMeta'
import { getShortenId } from '@/shared/utils/getShortenId'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'
import { Link } from 'react-router-dom'

type Props = {
  onAction?: () => void
  withdraw:
    | BaseTraderWithdrawModel
    | TraderAcceptedWithdrawModel
    | StartPayingWithdrawModel
    | PaidWithdrawModel
  showPayoutButtonOnMobile?: boolean
}

export const MobileBlockTraderWithdraw = ({
  withdraw,
  onAction,
  showPayoutButtonOnMobile = true,
}: Props) => {
  const subtitleClasses = 'text-text-secondary'
  const dataClasses = 'font-medium'
  const dataRowClasses = 'flex justify-between'
  const { id } = withdraw
  const requisite =
    'paymentRequisitValue' in withdraw ? formatRequisite(withdraw.paymentRequisitValue) : '—'
  const commission =
    'factTraderCommissionTo' in withdraw
      ? withdraw.factTraderCommissionTo
      : withdraw.traderCommissionAmount

  let columnsData = [
    {
      title: 'ID сделки:',
      data: (
        <CopyBlock text={id} title={id}>
          <Link to={id} className="text-primary">
            {getShortenId(id, 'long')}
          </Link>
        </CopyBlock>
      ),
      copyBtn: false,
    },
    {
      title: 'Курс:',
      data: withdraw.exchangeRateFromTo ? `${withdraw.exchangeRateFromTo} RUB` : '—',
    },
    {
      title: 'Банк:',
      data: getBankMeta(withdraw.bankToName).title,
    },
    {
      title: 'Реквизит:',
      data: (
        <CopyBlock text={id} title={id}>
          {requisite ? formatRequisite(requisite) : '—'}
        </CopyBlock>
      ),
    },
    ...('recipientFullName' in withdraw
      ? [
          {
            title: 'Получатель:',
            data: withdraw.recipientFullName,
          },
        ]
      : []),
  ]

  if (![WithdrawStatus.Paid, WithdrawStatus.PaidPartially].includes(withdraw.withdrawStatus)) {
    columnsData = columnsData.filter((c) => c.title !== 'Курс:')
  }

  const subtitle =
    withdraw.withdrawStatus === WithdrawStatus.WaitingProcessing
      ? formatDate(withdraw.assignedToTraderAt)
      : [WithdrawStatus.InProgress, WithdrawStatus.PayingRightNow].includes(withdraw.withdrawStatus)
        ? 'acceptedByTraderAt' in withdraw
          ? formatDate(withdraw.acceptedByTraderAt)
          : ''
        : [WithdrawStatus.PaidPartially, WithdrawStatus.Paid].includes(withdraw.withdrawStatus)
          ? 'confirmedByTraderAt' in withdraw
            ? formatDate(withdraw.confirmedByTraderAt ?? withdraw.acceptedByTraderAt)
            : ''
          : ''

  const timeLeft =
    [
      WithdrawStatus.InProgress,
      WithdrawStatus.PayingRightNow,
      WithdrawStatus.WaitingProcessing,
    ].includes(withdraw.withdrawStatus) && 'payingAllowedTill' in withdraw
      ? withdraw.payingAllowedTill
      : undefined

  const statusForTime =
    withdraw.withdrawStatus === WithdrawStatus.WaitingProcessing
      ? 'pending'
      : [WithdrawStatus.InProgress, WithdrawStatus.PayingRightNow].includes(withdraw.withdrawStatus)
        ? 'inProgress'
        : undefined

  const buttonStatus = WithdrawStatusButtons[withdraw.withdrawStatus]

  return (
    <BaseCard variant="secondary" className="flex flex-col mb-4 last:mb-0 px-5 text-sm">
      <div className="flex justify-between items-center gap-1 mb-6">
        <Link to={id}>
          <ItemLayout
            icon={<PayoutIcon status={WithdrawStatusIcons[withdraw.withdrawStatus]} />}
            title={<span className="mr-2">{WithdrawStatusTitles[withdraw.withdrawStatus]}</span>}
            subtitle={subtitle}
            titleClasses="font-medium"
            timeLeft={timeLeft}
            statusForTime={statusForTime}
          />
        </Link>
      </div>

      {withdraw.withdrawStatus !== WithdrawStatus.WaitingProcessing && (
        <div>
          <div className={`${dataRowClasses} mb-4`}>
            <CopyBlock
              text={withdraw.amountTo?.toFixed(2)}
              className="font-klein font-medium text-[18px]"
            >
              {formatCurrency(withdraw.amountTo ?? 0, withdraw.currencyTo)}
            </CopyBlock>
            {'currencyFrom' in withdraw && 'amountFrom' in withdraw && (
              <CopyBlock text={withdraw.amountFrom?.toFixed(2)} color="secondary">
                {formatCurrency(withdraw.amountFrom ?? 0, withdraw.currencyFrom)}
              </CopyBlock>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            {columnsData.map((el, i) => (
              <div key={i} className={dataRowClasses}>
                <span className={subtitleClasses}>{el.title}</span>
                <div className="flex items-center gap-1">
                  <span
                    className={
                      el.copyBtn && typeof el.data === 'string'
                        ? `${dataClasses} text-primary`
                        : `${dataClasses}`
                    }
                  >
                    {el.copyBtn && typeof el.data === 'string'
                      ? getShortenId(el.data, 'long')
                      : el.data}
                  </span>
                  {el.copyBtn && typeof el.data === 'string' && (
                    <BaseCopyButton text={el.data} color="primary" size="mini" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {withdraw.withdrawStatus === WithdrawStatus.WaitingProcessing && (
        <div>
          <div className={`${dataRowClasses} mb-4`}>
            <CopyBlock
              text={withdraw.amountTo?.toFixed(2)}
              className="font-klein font-medium text-[18px]"
            >
              {formatCurrency(withdraw.amountTo ?? 0, withdraw.currencyTo)}
            </CopyBlock>
          </div>
        </div>
      )}

      {withdraw.withdrawStatus === WithdrawStatus.Paid &&
        ('factTraderCommissionTo' in withdraw || 'traderCommissionAmount' in withdraw) &&
        ('factAmountTo' in withdraw || 'amountTo' in withdraw) &&
        (('factAmountTo' in withdraw ? withdraw.factAmountTo : 0) ??
          ('amountTo' in withdraw ? withdraw.amountTo : 0)) > 0 && (
          <div className="pt-4 border-t">
            <span className="block mb-[10px] font-klein font-medium">Полученная прибыль:</span>
            <div className="flex justify-between items-center">
              <CopyBlock
                text={commission?.toFixed(2)}
                className="font-klein font-medium text-[18px]"
              >
                {formatCurrency(commission ?? 0, Currencies.USDT)}
              </CopyBlock>
            </div>
          </div>
        )}

      {showPayoutButtonOnMobile && withdraw.withdrawStatus !== WithdrawStatus.Paid && (
        <PayoutButton className="mt-[30px]" status={buttonStatus} onClick={onAction} />
      )}
    </BaseCard>
  )
}
